<template>
    <div class="subscribe-component">
        <h1 class="pt-0">Choose your membership</h1>

        <div class="container">
            <div class="row">
                <div class="col-md-12" v-if="isInDowngradeState">
                    <b-alert variant="warning" show class="mb-2">
                        You already have a pending downgrade on your account. You are due to downgrade to 
                        <strong>{{ downgradePlan.tier.charAt(0).toUpperCase() + downgradePlan.tier.slice(1) }} 
                            ({{ downgradePlan.unit_length == 'year' ? 'Annual' : 'Monthly' }})</strong>
                        on <strong>{{ subscription.currentPeriodEndFormatted }}</strong>.
                    </b-alert>
                </div>
                <!-- Plan Details -->
                <div class="col-lg-6 col-md-12 mb-4">
                    <div class="benefits pb-2">
                        <template v-if="canShowPlanDetails">
                            <div class="mb-4" v-if="subscriptionChangeType.type != 'new'">
                                <b-alert
                                    show
                                    class="mt-0"
                                    :variant="subscriptionChangeType.type === 'downgrade' ? 'secondary' : 
                                            subscriptionChangeType.type === 'invalid' ? 'danger' :
                                            subscriptionChangeType.type === 'same' ? 'secondary' : 'secondary'"
                                >
                                    {{ subscriptionChangeType.message }}
                                </b-alert>
                            </div>

                            <div class="mt-2">
                                <h3>{{ tier.charAt(0).toUpperCase() + tier.slice(1) }}</h3>
                                <p>
                                    {{ term.charAt(0).toUpperCase() + term.slice(1) }}
                                </p>

                                <div class="my-3 pt-3 border-top border-bottom">
                                    <template v-if="term == 'monthly'">
                                        <p class="text-bold">£{{ plan.planPrice.price }}<small>/month</small></p>
                                    </template>
                                    <template v-else>
                                        <p>
                                            <span class="old-price">£{{ monthlyPriceSameTier.toFixed(2) }}</span>
                                            <strong>£{{ (Math.floor((plan.planPrice.price/12) * 100) / 100).toFixed(2) }}<small>/month</small></strong>
                                        </p>
                                        <p>Billed annually.</p>
                                    </template>
                                </div>

                                <template v-if="proRataAmount !== null && (subscriptionChangeType.type === 'upgrade' || subscriptionChangeType.type === 'cycle')">
                                    <div class="alert alert-secondary mb-3">
                                        <p class="mb-1"><strong>Pro-rata Adjustment</strong></p>
                                        <p class="mb-1">
                                            Your immediate payment will be: <strong>£{{ (proRataAmount/100).toFixed(2) }}</strong>
                                        </p>
                                    </div>
                                </template>

                                <p>
                                    <template v-if="billingMessage.message">
                                        <strong>{{ billingMessage.message }}</strong>
                                    </template>
                                </p>
                            </div>
                        </template>
                        <template v-else>
                            <p>You are already due to downgrade to this plan.</p>
                        </template>
                    </div>
                </div>

                <!-- Right Section: Subscription Flow -->
                <div class="col-lg-6 col-md-12" v-if="canSubscribe">
                    <template v-if="!isAuth">
                        <template v-if="!isTrialExpired">
                            <h2 class="text-center text-white h3">Create a free account</h2>
                        </template>

                        <!-- SSO Options -->
                        <sso-component register-url="/register" :return-url="returnUrl"></sso-component>
                    </template>

                    <!-- Existing code for authenticated users -->
                    <template v-else>
                        <template v-if="!hasBegun">
                            <div>
                                <template v-if="!paymentMethod">
                                    <!-- Show card-update-component if no payment method -->
                                    <div class="mb-2 text-center font-weight-bold text-white">Please add a payment method to continue.</div>
                                    <card-update-component
                                        :is-inline="true"
                                        :with-cancel="allowCancelling"
                                        :block="true"
                                        :amount="amountToPaySubUnit"
                                    ></card-update-component>
                                </template>
                                <template v-else>
                                    <!-- Subscription flow content -->
                                    <b-alert variant="danger" :show="hasError" class="mb-2">{{ error }}</b-alert>

                                    <div class="text-center text-white">
                                        <template v-if="!isShowUpdatePaymentMethod">
                                            <div class="my-3">
                                                <div class="mb-3">
                                                    <template v-if="billingMessage.message">
                                                        <strong>{{ billingMessage.message }}</strong>
                                                    </template>
                                                </div>
                                                <div class="mb-4" v-if="discount.message">
                                                    {{ discount.message }}
                                                </div>

                                                <div class="d-inline-block text-center">
                                                    <div class="payment-btn-wrapper">
                                                        <span class="text-monospace ml-3">
                                                            <span class="mr-3">{{ paymentMethod.card_type.charAt(0).toUpperCase() + paymentMethod.card_type.slice(1) }}</span>
                                                            **** **** **** {{ paymentMethod.last4 }}
                                                        </span>
                                                    </div>

                                                    <div class="mb-3 mt-1">
                                                        <b-button variant="link"
                                                                  @click="isShowUpdatePaymentMethod = true"
                                                                  v-if="!isShowUpdatePaymentMethod"
                                                                  class="text-white">Update payment method</b-button>
                                                    </div>
                                                </div>

                                                <div class="my-2" v-if="subscriptionChangeType.type == 'new'">
                                                    <stripe-coupon-component :selected-plan="plan"></stripe-coupon-component>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="mb-2 text-center font-weight-bold">If you have a voucher code this can be added after you have added your payment method.</div>
                                            <card-update-component
                                                v-if="isShowUpdatePaymentMethod"
                                                :is-inline="true"
                                                :with-cancel="allowCancelling"
                                                :block="true"
                                                :amount="amountToPaySubUnit"
                                            ></card-update-component>
                                        </template>

                                        <template v-if="paymentMethod && !isShowUpdatePaymentMethod">
                                            <b-form-checkbox
                                                v-model="tandc"
                                                name="tandc"
                                                value="accepted"
                                                unchecked-value="not_accepted"
                                                class="my-3 text-white">
                                                I consent to PLAYvirtuoso <a href="https://playvirtuoso.com/tandc/" target="_blank" class="text-underline">terms and conditions</a>.
                                            </b-form-checkbox>

                                            <b-button variant="primary" :disabled="isBusy" @click="beginSubscribeFlow(plan.id)">
                                                {{ isTrialExpired ? 'Subscribe' : 'Start trial' }}
                                            </b-button>
                                        </template>
                                    </div>
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <b-alert variant="info" show>
                                It looks like you have a pending subscription. Please try again in a few hours.
                            </b-alert>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { globalEventFire } from '../tracker.js';

export default {
    props: {
        plan: {
            type: Object,
            required: false,
        },
        tier: {
            type: String,
            required: true,
        },
        term: {
            type: String,
            required: true,
        },
        monthlyPriceSameTier: {
            type: Number,
            required: false,
        },
        subscription: {
            type: Object,
            required: false,
        },
        currentPlan: {
            type: Object,
            required: false,
        },
        currentTier: {
            type: String,
            required: false,
        },
        returnUrl: {
            type: String,
            required: false,
        },
        hasPaymentMethod: {
            type: Boolean,
            required: true,
        },
        hasBegun: {
            type: Boolean,
            required: true,
        },
        returnUrl: {
            type: String,
            required: true,
        },
        isInDowngradeState: {
            type: Boolean,
            required: true,
        },
        downgradePlan: {
            type: Object,
            required: false,
        }
    },

    data() {
        return {
            stripeAPIToken: document.querySelector('meta[name="stripe-key"]').getAttribute('content'),
            stripe: '',
            isBusy: false,
            error: false,
            hasError: false,
            hardError: false,
            tandc: 'not_accepted',
            isShowUpdatePaymentMethod: false,
            paymentMethod: null,
            isAuth: false,
            isVerified: false,
            isSubscribed: false,
            user: {},
            discount: {
                amount: 0,
                message: '',
                id: null,
                code: null
            },
            isSubscribeFlowOpen: false,
            isTrialExpired: false,
            allowCancelling: false,
            resubscribePlan: false,
            proRataAmount: null,
            currentPeriodEnd: null,
        };
    },

    computed: {
        amountToPay() {
            let amt = 0;

            if (!this.isTrialExpired) {
                return amt;
            }

            amt = this.amountAfterTrial;
            return parseFloat(amt);
        },

        amountAfterTrial() {
            let amt = 0;

            if (this.plan.planPrice) {
                amt = this.plan.planPrice.price;
            }

            // Apply discount if any
            amt = amt - this.discount.amount;
            amt = parseFloat(Math.round(amt * 100) / 100).toFixed(2);

            return amt;
        },

        amountToPaySubUnit() {
            return this.amountToPay * 100;
        },

        trialExpiryDate() {
            // 7 days in the future
            var d = new Date();
            d.setDate(d.getDate() + 7);

            var day = d.getDate();
            var month = d.getMonth() + 1; // Months are zero-indexed in JS, so add 1
            var year = d.getFullYear().toString().slice(-2); // Get last 2 digits of the year

            return `${day}.${month}.${year}`;
        },

        formattedPeriodEndDate() {
            if (!this.subscription?.current_period_end) return '';
            
            const date = new Date(this.subscription.current_period_end);
            return date.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long',
                year: 'numeric'
            });
        },

        subscriptionCompleteUrl() {
            if (this.returnUrl && this.returnUrl.length > 0) {
                try {
                    const url = new URL(this.returnUrl, window.location.origin);
                    return url.pathname + (url.search ? url.search : '');
                } catch (error) {
                    return this.returnUrl;
                }
            }

            if(this.subscriptionChangeType.type === 'new') {
                return '/onboarding?new=true';
            }
            return '/dashboard?upgraded=true';
        },

        subscriptionChangeType() {
            if (!this.currentTier || this.currentTier === 'freemium') {
                return {
                    type: 'new',
                    message: `Subscribe to ${this.tier.charAt(0).toUpperCase() + this.tier.slice(1)} plan with ${this.term} billing`
                };
            }
            
            const isCurrentAnnual = this.currentPlan?.unit_length === 'year' || 
                    (this.currentPlan?.unit_length === 'month' && this.currentPlan?.unit_num === 12);
                    
            const isRequestedAnnual = this.term === 'annual';

            // Going from essentials to premium
            if(this.currentTier === 'essentials' && this.tier === 'premium') {
                if(isCurrentAnnual) {
                    if(!isRequestedAnnual) {
                        return {
                            type: 'invalid',
                            message: 'You cannot switch from annual to monthly billing when moving from essentials to premium'
                        };
                    }
                }
            }

            if (this.currentTier === this.tier) {
                
                if (isCurrentAnnual && this.term === 'monthly') {
                    return {
                        type: 'invalid',
                        message: 'You cannot switch from annual to monthly billing'
                    };
                }
                
                if ((isCurrentAnnual && isRequestedAnnual) || (!isCurrentAnnual && !isRequestedAnnual)) {
                    return {
                        type: 'same',
                        message: 'You are already subscribed to this plan'
                    };
                }
                
                return {
                    type: 'cycle',
                    message: 'You are changing from monthly to annual billing'
                };
            }

            const tierLevels = { 'freemium': 0, 'essentials': 1, 'premium': 2 };
            const currentLevel = tierLevels[this.currentTier];
            const newLevel = tierLevels[this.tier];

            if (newLevel > currentLevel) {
                return {
                    type: 'upgrade',
                    message: `Upgrade from ${this.currentTier} to ${this.tier} plan`
                };
            }

            return {
                type: 'downgrade',
                message: `Downgrade to ${this.tier} plan at the end of your billing cycle (${this.formattedPeriodEndDate}). Your next payment will be £${this.amountAfterTrial}.`
            };
        },

        billingMessage() {
            let message = '';
            
            console.log('Subscription change type:', this.subscriptionChangeType.type);
            console.log('Is trial expired:', this.isTrialExpired);
            console.log('Pro-rata amount:', this.proRataAmount);
            console.log('Amount after trial:', this.amountAfterTrial);

            if (this.subscriptionChangeType.type === 'cycle') {
                if (this.proRataAmount !== null) {
                    message = `You will be charged £${(this.proRataAmount/100).toFixed(2)} today to switch to annual billing.`;
                } else {
                    message = `Your next annual payment will be £${this.amountAfterTrial}.`;
                }
            } else if (this.subscriptionChangeType.type === 'new') {
                if (this.isTrialExpired) {
                    message = `You will be charged £${this.amountToPay} today.`;
                } else {
                    message = `After your ${this.trialExpiryDate} trial, you will be charged £${this.amountAfterTrial}.`;
                }
            } else if (this.subscriptionChangeType.type === 'upgrade') {
                if (this.proRataAmount !== null) {
                    message = `You will be charged £${(this.proRataAmount/100).toFixed(2)} today for the pro-rata adjustment. Your next regular payment will be £${this.amountAfterTrial}.`;
                } else {
                    message = `You will be charged £${this.amountAfterTrial}.`;
                }
            } else if (this.subscriptionChangeType.type === 'downgrade') {
                message = `Your plan will be downgraded at the end of your current billing period (${this.formattedPeriodEndDate}). Your next payment will be £${this.amountAfterTrial}.`;
            } else if (this.subscriptionChangeType.type === 'resubscribe') {
                message = `You will be charged £${this.amountToPay} today.`;
            }

            console.log('Billing message:', message);

            return {
                message: message
            };
        },

        subscriptionEndpoint() {
            let endpoint = {
                path: '',
                params: {
                    plan_id: this.plan.id
                }
            };

            // Add discount info if present
            if (this.discount.id) {
                endpoint.params.discount_amount = this.discount.amount;
                endpoint.params.coupon_id = this.discount.id;
                endpoint.params.coupon_code = this.discount.code;
            }

            // Add resubscribe flag if needed
            if (this.resubscribePlan) {
                endpoint.params.resubscribe = true;
            }

            // For downgrades
            if (this.subscriptionChangeType.type === 'downgrade') {
                endpoint.path = '/api/billing/schedule-downgrade';
                endpoint.params.effective_date = this.subscription?.current_period_end;
                return endpoint;
            }

            // For upgrades from paid plan (including monthly to annual)
            if ((this.subscriptionChangeType.type === 'upgrade' && this.currentTier !== 'freemium') ||
                this.subscriptionChangeType.type === 'cycle') {
                endpoint.path = '/api/billing/upgrade-plan';
                endpoint.params.prorate = true;
                endpoint.params.current_plan_id = this.currentPlan?.id;
                return endpoint;
            }

            // For new subscriptions (including upgrades from freemium)
            endpoint.path = '/api/billing/start-subscription/' + this.plan.id;
            endpoint.params.amount_to_pay = this.amountToPay;
            return endpoint;
        },

        canShowPlanDetails() {
            // First, check if this is a downgrade type
            if (this.subscriptionChangeType.type !== 'downgrade') {
                return true;
            }

            // Then, if we're in a downgrade state, check if this is the plan we're downgrading to
            if (this.isInDowngradeState) {
                const isDowngradePlan = this.downgradePlan.id === this.plan.id;
                
                if (isDowngradePlan) {
                    return false;
                }
            }

            // If we got here, we can show the plan details
            console.log('Can show plan details');
            return true;
        },

        canSubscribe() {
            if (!this.canShowPlanDetails) {
                return false;
            }

            const isCurrentAnnual = this.currentPlan?.unit_length === 'year' || 
                (this.currentPlan?.unit_length === 'month' && this.currentPlan?.unit_num === 12);
            
            const isRequestedAnnual = this.term === 'annual';

            if (this.currentTier === this.tier) {                
                // Can't subscribe if:
                // 1. Trying to go from annual to monthly
                // 2. Already on the same billing cycle
                return !(
                    (isCurrentAnnual && this.term === 'monthly') || // Can't go from annual to monthly
                    ((isCurrentAnnual && isRequestedAnnual) || (!isCurrentAnnual && !isRequestedAnnual)) // Already on same cycle
                );
            }

            // Can't subscribe if going from essentials annual to premium monthly
            if(this.currentTier === 'essentials' && this.tier === 'premium') {
                if(isCurrentAnnual) {
                    if(!isRequestedAnnual) {
                        return false;
                    }
                }
            }
            
            return true;
        },
    },

    mounted() {
        // User logged in?
        if (window.virtuoso) {
            if (window.virtuoso.is_auth) {
                this.isAuth = true;
                this.isVerified = window.virtuoso.is_verified;
                this.isSubscribed = window.virtuoso.is_subscribed;
                this.user = window.virtuoso.user;
                //this.isTrialExpired = window.virtuoso.is_trial_expired;
                this.isTrialExpired = true; // Hard-code this as it's the same as not having trials
            }
        }

        this.includeStripe('js.stripe.com/v3/', () => {
            this.configureStripe();
        });

        if (this.isAuth) {
            this.loadPaymentMethod();
            this.fetchProRataAmount(); // Add this line to fetch pro-rata amount on mount
        }

        // When payment methods have been updated refresh that section
        Bus.$on("updatedPaymentMethods", () => {
            this.loadPaymentMethod();
            this.isShowUpdatePaymentMethod = false;
        });

        Bus.$on("discountUpdated", (d) => {
            this.discount.id = d.id;
            this.discount.code = d.code;
            this.discount.amount = d.discountAmount;
            this.discount.message = d.descriptionGenerated;
        });

        Bus.$on("cancelPaymentMethod", () => {
            this.loadPaymentMethod();
            this.isShowUpdatePaymentMethod = false;
        });
    },

    methods: {
        includeStripe(URL, callback) {
            let documentTag = document, tag = 'script',
                object = documentTag.createElement(tag),
                scriptTag = documentTag.getElementsByTagName(tag)[0];
            object.src = '//' + URL;
            if (callback) {
                object.addEventListener('load', function (e) {
                    callback(null, e);
                }, false);
            }
            scriptTag.parentNode.insertBefore(object, scriptTag);
        },

        configureStripe() {
            this.stripe = Stripe(this.stripeAPIToken);
        },

        loadPaymentMethod() {
            this.isBusy = true;
            axios.get('/api/me/payment_method')
                .then((response) => {
                    this.paymentMethod = response.data || null;
                    this.isBusy = false;

                    if (this.paymentMethod) {
                        this.isShowUpdatePaymentMethod = false;
                        this.allowCancelling = true;
                    } else {
                        this.isShowUpdatePaymentMethod = true;
                        this.allowCancelling = false;
                    }
                })
                .catch(() => {
                    this.paymentMethod = null;
                    this.isBusy = false;
                });
        },

        async fetchProRataAmount() {
            // Only fetch if user is authenticated, has a subscription, and is upgrading
            if (!this.isAuth || !this.isSubscribed || (this.subscriptionChangeType.type !== 'upgrade' && this.subscriptionChangeType.type !== 'cycle')) {
                this.proRataAmount = null;
                this.currentPeriodEnd = null;
                return;
            }

            try {
                const response = await axios.post('/api/billing/calculate-pro-rata', {
                    targetPlan: this.plan.id,
                });
                
                console.log('Pro-rata response:', response.data);
                
                if (response.data.success) {
                    this.proRataAmount = response.data.data.amount;
                    this.currentPeriodEnd = response.data.data.current_period_end;
                    console.log('Set pro-rata amount:', this.proRataAmount);
                }
            } catch (error) {
                console.error('Error fetching pro-rata amount:', error);
                this.error = error.response?.data?.message || 'Failed to calculate pro-rata amount';
                this.hasError = true;
            }
        },

        // Method to open the subscription flow inline
        openSubscribeFlow(isPlanSelection = false) {
            this.orderedPlanText = this.term.charAt(0).toUpperCase() + this.term.slice(1) + ' ' + this.tier.charAt(0).toUpperCase() + this.tier.slice(1) + ' membership';
            this.isSubscribeFlowOpen = true;
        },

        // Subscribe them
        beginSubscribeFlow(planId) {
            if (this.tandc == 'not_accepted') {
                this.hasError = true;
                this.error = 'You must accept the terms and conditions to continue.';
            } else {
                this.error = '';
                this.isBusy = true;
                this.hasError = false;

                let url = this.subscriptionEndpoint.path;
                let params = this.subscriptionEndpoint.params;

                axios.post(url, params)
                    .then((response) => {
                        console.log(response);
                        if (response.error || response.data.error) {
                            this.hasError = true;
                            this.error = response.error ? response.error : response.data.error;
                        } else {
                            // For new subscriptions and upgrades
                            if (response.data.subscription) {
                                this.handleSubscription(response.data.subscription);
                            } 
                            // For downgrades and upgrades
                            else if (response.data.data && response.data.success) {
                                setTimeout(() => {
                                    location.href = this.subscriptionCompleteUrl + '&plan-change=true';
                                }, 2500);
                            }
                            // Reset form state
                            this.isProcessing = false;
                            this.setupIntent = null;
                        }
                    });
            }
        },

        handleSubscription(subscription) {
            const {latest_invoice} = subscription;
            const {payment_intent} = latest_invoice;

            if (payment_intent) {
                const {client_secret, status} = payment_intent;

                if (status === 'requires_action') {
                    this.stripe.confirmCardPayment(client_secret).then((result) => {
                        if (result.error) {
                            this.hasError = true;
                            this.error = result.error.message;
                            this.isBusy = false;

                            // Delete the subscription
                            axios.post('/api/billing/delete-subscription', {
                                subscriptionId: subscription.latest_invoice.lines.data[0].metadata.virtuoso_subscription_id
                            });
                        } else {
                            this.fireAnalytics(subscription);

                            setTimeout(() => {
                                location.href = this.subscriptionCompleteUrl + '&3ds=true';
                            }, 2500);
                        }
                    });
                } else {
                    this.fireAnalytics(subscription);

                    setTimeout(() => {
                        location.href = this.subscriptionCompleteUrl;
                    }, 2500);
                }
            } else {
                if (subscription.status == 'trialing') {
                    this.fireAnalytics(subscription);

                    setTimeout(() => {
                        location.href = this.subscriptionCompleteUrl;
                    }, 2500);
                }
            }
        },

        // Fire analytics
        fireAnalytics(subscription) {
            globalEventFire('Subscribe', 'Sub', {
                'category': 'Subscription',
                'name': subscription.plan.nickname,
                'item_id': this.plan.id,
                'transaction_id': subscription.latest_invoice.lines.data[0].metadata.virtuoso_subscription_id ?? 0,
                'amount': this.amountToPay,
            });
        },
    },
    watch: {
        tier() {
            this.fetchProRataAmount();
        },
        term() {
            this.fetchProRataAmount();
        }
    }
}
</script>
